/**
 * Wissen (Roadmap V-13): Typen, Status, Fortschritt und die Umstellung der
 * alten Buecher - rein und getestet. Dieselbe Umstellung laeuft beim
 * Datenbank-Upgrade (Version 19) und beim Einspielen alter Sicherungen
 * (Format 4 -> 5).
 */
import { notizBloecke } from '@/domain/notes/bloecke';
import type { Book, BookStatus, Scale5, WissensTyp } from '@/domain/types';

export const WISSENS_TYPEN: { key: WissensTyp; label: string; einheit: string }[] = [
  { key: 'buch', label: 'Buch', einheit: 'Seiten' },
  { key: 'hoerbuch', label: 'Hörbuch', einheit: 'Minuten' },
  { key: 'video', label: 'YouTube-Video', einheit: 'Minuten' },
  { key: 'podcast', label: 'Podcast', einheit: 'Minuten' },
  { key: 'artikel', label: 'Artikel/Blogbeitrag', einheit: 'Abschnitte' },
  { key: 'studie', label: 'Studie/Bericht', einheit: 'Seiten' },
  { key: 'kurs', label: 'Online-Kurs', einheit: 'Lektionen' },
  { key: 'doku', label: 'Dokumentation', einheit: 'Minuten' },
  { key: 'sonstiges', label: 'Sonstiges', einheit: 'Teile' },
];

export const typInfo = (typ: WissensTyp) => WISSENS_TYPEN.find((t) => t.key === typ) ?? WISSENS_TYPEN.at(-1)!;

/** Die Board-Spalten (V-13); "Abgebrochen" steht unter "Abgeschlossen". */
export const WISSENS_STATUS: { key: BookStatus; label: string }[] = [
  { key: 'wunsch', label: 'Will ich aufnehmen' },
  { key: 'lese', label: 'In Bearbeitung' },
  { key: 'gelesen', label: 'Abgeschlossen' },
  { key: 'abgebrochen', label: 'Abgebrochen' },
];

export const statusLabel = (s: BookStatus) => WISSENS_STATUS.find((x) => x.key === s)!.label;

/** Nachbarspalte fuer die Pfeilknoepfe; Abgebrochen liegt bei Abgeschlossen. */
export function nachbarStatus(s: BookStatus, richtung: 1 | -1): BookStatus | null {
  const reihe: BookStatus[] = ['wunsch', 'lese', 'gelesen'];
  const i = reihe.indexOf(s === 'abgebrochen' ? 'gelesen' : s);
  return reihe[i + richtung] ?? null;
}

/** Anteil 0..1 oder `null`, wenn kein Umfang angegeben ist. */
export function fortschrittAnteil(f: Book['fortschritt']): number | null {
  if (!f || !(f.gesamt > 0)) return null;
  return Math.min(1, Math.max(0, f.aktuell / f.gesamt));
}

/** Nur http(s)-Links; alles andere (auch `javascript:`) wird verworfen. */
export function linkGueltig(eingabe: string | undefined): string | undefined {
  const t = eingabe?.trim();
  if (!t) return undefined;
  try {
    const u = new URL(/^[a-z]+:/i.test(t) ? t : `https://${t}`);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** Bewertung 1-10 (Leitlinie 6); alles andere faellt weg. */
export function bewertungGueltig(v: unknown): number | undefined {
  return Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 10 ? (v as number) : undefined;
}

type AltesBuch = Partial<Book> & { id: string; areaId?: string | null; rating?: number };

/**
 * Alte Buecher -> Wissensquellen: Typ "Buch", Bereich in `areaIds`,
 * Bewertung 1-5 verdoppelt (Original bleibt in `ratingLegacy5`).
 * Bereits umgestellte Eintraege (mit `typ`) bleiben unveraendert.
 */
export function wissenUmstellen<T extends AltesBuch>(rows: T[]): T[] {
  return rows.map((b) => {
    if (b.typ) return b;
    const neu: AltesBuch = {
      ...b,
      typ: 'buch',
      tags: Array.isArray(b.tags) ? b.tags : [],
      areaIds: b.areaId ? [b.areaId] : [],
      areaId: null,
    };
    if (typeof b.rating === 'number' && b.rating >= 1 && b.rating <= 5) {
      neu.ratingLegacy5 = b.rating as Scale5;
      neu.rating = b.rating * 2;
    } else {
      delete neu.rating;
    }
    return neu as T;
  });
}

/**
 * Lokale Suche der Wissensseite: Titel, Urheber, Schlagwoerter, Notizen,
 * Erkenntnisse und Zitate (F-20). Die globale Suche folgt mit F-18.
 */
export function passtZurSuche(b: Book, suche: string): boolean {
  const q = suche.trim().toLocaleLowerCase('de');
  if (!q) return true;
  const text = [
    b.title, b.author, b.notes, b.erkenntnisse, ...(b.tags ?? []),
    ...(b.stellen ?? []).flatMap((s) => [s.ort, s.text]),
  ].filter(Boolean).join(' ').toLocaleLowerCase('de');
  return q.split(/\s+/).every((wort) => text.includes(wort.replace(/^#/, '')));
}

/**
 * Die einzelnen Erkenntnisse aus dem Textfeld (F-20): jeder Listenpunkt und
 * jeder Absatz fuer sich - damit sich jede einzeln umsetzen laesst.
 */
export function erkenntnisPunkte(text: string | undefined): string[] {
  if (!text) return [];
  return notizBloecke(text)
    .flatMap((b) => (b.art === 'liste' ? b.punkte : [b.zeilen.join(' ')]))
    .map((p) => p.trim())
    .filter(Boolean);
}

/** Typ-Vorschlag fuer einen eingefuegten Link (F-17): YouTube ist ein Video, sonst ein Artikel. */
export function typAusLink(url: string): WissensTyp {
  try {
    const host = new URL(url).hostname.replace(/^www\./, '').replace(/^m\./, '');
    return host === 'youtube.com' || host === 'youtu.be' ? 'video' : 'artikel';
  } catch {
    return 'sonstiges';
  }
}
